import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { mergeRegister } from "@lexical/utils";
import {
  $getNodeByKey,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  type NodeKey,
} from "lexical";
import { useCallback, useEffect } from "react";

export const useSelectable = (nodeKey: NodeKey): boolean => {
  const [editor] = useLexicalComposerContext();
  const [selected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);

  useEffect(
    () =>
      editor.registerCommand(
        CLICK_COMMAND,
        (event: MouseEvent) => {
          if (editor.getElementByKey(nodeKey)?.contains(event.target as Element)) {
            if (event.shiftKey) {
              setSelected(!selected);
            } else {
              clearSelection();
              setSelected(true);
            }
            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor, selected, setSelected, clearSelection, nodeKey],
  );

  const handleBackspace = useCallback(
    (event: Event) => {
      if (selected) {
        event.preventDefault();
        editor.update(() => {
          $getNodeByKey(nodeKey)?.remove();
        });
      }
      return false;
    },
    [editor, selected, nodeKey],
  );

  const handleDelete = useCallback(
    (event: Event) => {
      if (selected) {
        event.preventDefault();
        editor.update(() => {
          const node = $getNodeByKey(nodeKey);
          if (node?.getNextSibling()) {
            node.selectNext();
          }
          $getNodeByKey(nodeKey)?.remove();
        });
      }
      return false;
    },
    [editor, selected, nodeKey],
  );

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(KEY_BACKSPACE_COMMAND, handleBackspace, COMMAND_PRIORITY_LOW),
        editor.registerCommand(KEY_DELETE_COMMAND, handleDelete, COMMAND_PRIORITY_LOW),
      ),
    [editor, handleBackspace, handleDelete],
  );

  return selected;
};
